import _, {get, set, keyBy} from 'lodash';
import {Map as ImmMap, Set as ImmSet} from 'immutable';
import {
  Expressions,
  Types,
  matchExpression,
  type Type,
  type ExpressionPath,
  getSystem,
  withSystem,
  Plugins,
  ElementPreset,
  Component,
} from '@packages/systems/wfdl';
import {
  EElement,
  ELiteral,
  ETyped,
  EText,
  type Expression,
  type ExpressionElement,
  EList,
} from '@packages/systems/wfdl-ast';
import {
  FileRecord,
  type RawFileType,
  FileRecordType,
} from '@packages/systems/core/records/FileRecord';
import type {RawStyleBlockType} from '@packages/systems/style-legacy/values';
import {getObject, getString} from '@packages/utilities/json';
import {updateDefaultDataPropertiesOnComponent} from '@packages/systems/core/component-conversions';
import {parseDefaultData} from '@packages/systems/core/render';
import {mapSlotDefaultLists} from '@packages/utilities/platform-slots';
import {constant, concat, identity} from '@packages/utilities/fp/utils';
import {
  PluginName,
  getPluginName,
  namesEqual,
  pluginName,
  qualify,
  typeName,
  Qualified,
  getName,
} from '@packages/systems/wfdl-Qualified';
import {AssetsType} from '@packages/systems/designer/assets';
import {
  AssetExport,
  AssetMap,
  LibraryAssetManagementMap,
  LibraryExportHash,
  LibraryExportsHashMap,
  SymbolsLocalizationData,
} from '../types';
import {isLottieAsset, isRiveAsset} from '@packages/domains/assets/utilities';
import {
  isImageElement,
  isLottie as isLottieElement,
  isRive as isRiveElement,
} from '@packages/utilities/elementTypeUtils';
import type {JSONNode} from '@packages/shared/types/JSONNode';
import {traverseSymbolsLocalizationsData} from './localization';
import {getComponentByOrigin} from '@packages/utilities/platform';
import {StyleBlockStateType} from '@packages/shared/LegacyStyleSystem/states/StyleBlockStateTypes';
import {StyleBlock} from '@packages/systems/style-legacy/records';
import {replaceBackgroundImages} from '@packages/shared/LegacyStyleSystem/utils/StyleBlockUtils';
import {FolderT} from '@packages/systems/assetManager';
import {simpleHash} from './hashing';
import {getExtension} from '@packages/systems/assets';
import {LIBRARY_EXPORT_TYPES, getSupportedAssetTypes} from '../constants';
import {
  SUPPORTED_IMAGE_TYPES,
  SUPPORTED_VIDEO_TYPES,
} from '@packages/shared/constants/Assets';

export const LOTTIE_SRC_PATH = 'data.animation.val.path.src';
export const LOTTIE_ID_PATH = 'data.animation.val.path.id';
export const RIVE_SRC_PATH = 'data.animation.path.src';
export const RIVE_ID_PATH = 'data.animation.path.id';
export const BG_VIDEO_SRC_PATH = 'data.bgvideo.poster_image_url';
export const BG_VIDEO_ID_PATH = 'data.bgvideo.id';

export const IMAGE_ASSET_EXPRESSION_PATH: ExpressionPath = [
  {in: 'Record', at: 'img'},
];
export const LOTTIE_ASSET_EXPRESSION_PATH: ExpressionPath = [
  {in: 'Record', at: 'animation'},
  {in: 'Union', tag: 'value'},
  {in: 'Record', at: 'path'},
];
export const RIVE_ASSET_EXPRESSION_PATH: ExpressionPath = [
  {in: 'Record', at: 'animation'},
  {in: 'Record', at: 'path'},
];

export const imageType = qualify(pluginName('Asset'), typeName('Image'));
export const backgroundVideoType = qualify(
  pluginName('BackgroundVideo'),
  typeName('BackgroundVideoWrapper')
);
export const lottiePathType = qualify(
  pluginName('Animation'),
  typeName('LottiePath')
);
export const rivePathType = qualify(
  pluginName('Animation'),
  typeName('RivePath')
);

type Overrides = {[id: string]: {id: string}} | undefined;
type LottiePath = {
  id?: string;
  src: string;
  origFileName: string;
  totalDuration: number;
};
type RivePath = {
  id?: string;
  src: string;
  origFileName: string;
};

export const getAssetIdsFromStyleBlock = (
  styleBlock: RawStyleBlockType
): string[] => {
  const {styleLess, variants} = styleBlock;
  const styleLessStrings: Array<string> = [];

  if (styleLess) styleLessStrings.push(styleLess);
  if (variants) {
    Object.values(variants).forEach((variant) => {
      if (!variant.styleLess) return;
      styleLessStrings.push(variant.styleLess);
    });
  }

  // parse all assets in styleLess strings
  const assetIdUrls = styleLessStrings.flatMap((styles) => {
    const urls = styles.match(/\bimg_\S+/g);
    return urls ? urls : [];
  });

  // remove img_ prefix, commas & semicolons
  const assetIds = assetIdUrls.map((assetIdUrl) =>
    assetIdUrl.replace(/^img_/, '').replace(/[;,]$/, '')
  );

  return assetIds;
};

export const getAllAssetsToInclude = (
  render: Expression,
  dataType: Type,
  styleBlocks: RawStyleBlockType[],
  componentMap: Component.ComponentMap,
  symbolsLocalizations?: SymbolsLocalizationData,
  pluginId?: string
): NonNullable<ElementPreset.ElementPresetType['assetsIds']> => {
  const allStyleBlockAssetIds = styleBlocks.flatMap(getAssetIdsFromStyleBlock);
  const allElementAssetIds = getAssetIdsFromExpression(render);
  const allDataTypeAssetIds = getAssetIdsFromDataType(dataType);

  const allSymbolsLocalizationsAssetsIds = getAssetIdsFromSymbolsLocalizations(
    symbolsLocalizations,
    componentMap
  );

  const allAssetIds = allElementAssetIds
    .concat(allStyleBlockAssetIds)
    .concat(allDataTypeAssetIds)
    .concat(allSymbolsLocalizationsAssetsIds);

  return getAllAssetsAsPluginReferences(new Set(allAssetIds), pluginId);
};

function getAllAssetsAsPluginReferences(
  assetIds: Set<string>,
  pluginId: string = ''
): NonNullable<ElementPreset.ElementPresetType['assetsIds']> {
  const qualifiedAssetIds: ElementPreset.ElementPresetType['assetsIds'] = [];
  for (const id of assetIds) {
    qualifiedAssetIds.push(qualify(Plugins.name(pluginId), id));
  }
  return qualifiedAssetIds;
}

export const buildAssetMap = (
  assets: RawFileType[]
): Record<string, RawFileType> => {
  return assets.reduce<Record<string, RawFileType>>((memo, asset) => {
    memo[asset._id] = asset;
    return memo;
  }, {});
};

export const isImageType = (type: Type): boolean => {
  const typeConstructorName = Types.getTypeConstructor(type);
  return Boolean(
    typeConstructorName && namesEqual(typeConstructorName, imageType)
  );
};

export const getAssetIdsFromDataType = (
  dataType: Type,
  overrides?: Overrides
): string[] => {
  const fields = Types.getRecordFields(dataType);
  if (!fields) {
    return [];
  }
  const assetIds = new Set<string>();
  Object.entries(fields).forEach(([fieldKey, type]) => {
    if (isImageType(type)) {
      const defaultAsset: {id: string} | null = Types.getDefaultValue(
        type,
        null
      );
      // Collect default asset id from type
      if (defaultAsset?.id) {
        assetIds.add(defaultAsset.id);
      }

      // Collect asset id from override
      const override = overrides?.[fieldKey];
      if (override?.id) {
        assetIds.add(override.id);
      }
    }

    // Here we can add other types like Lottie, Rive, etc.
  });

  return Array.from(assetIds);
};

/**
 * Traverses the given SymbolsLocalizationData and collects all asset unique IDs
 * found on the JSON Nodes.
 */
export const getAssetIdsFromSymbolsLocalizations = (
  symbolsLocalizations: SymbolsLocalizationData = {},
  componentMap: Component.ComponentMap
): string[] => {
  const assets: Set<string> = new Set();

  traverseSymbolsLocalizationsData({
    symbolsLocalizations,
    matchSymbol(_locale, symbol) {
      const assetIds = collectAssetIdsFromJSONNode(symbol, componentMap);
      assetIds.forEach((id) => assets.add(id));
      return symbol;
    },
  });

  return Array.from(assets);
};

const getAssetIdFromSrc = (src: string): string | undefined => {
  const id = src.match(ASSET_ID_REGEX)?.[1];
  return id;
};

export const getAssetIdsFromExpression = Expressions.everything(concat)(
  matchExpression({
    Element: ({type, data}) => {
      // BackgroundVideo doesn't define its data with a Literal like Image or Lottie elements do.
      // Instead, it stores the asset reference directly in the Element's data structure,
      // so we need to extract and update it here.
      if (namesEqual(type, backgroundVideoType)) {
        const posterUrlExpr = Expressions.getIn(data, [
          {in: 'Record', at: 'bgvideo'},
          {in: 'Record', at: 'poster_image_url'},
        ]);
        if (!posterUrlExpr) return Expressions.resume([]);

        const posterUrl = Expressions.getText(posterUrlExpr) ?? '';
        const assetId = getAssetIdFromSrc(posterUrl);

        return Expressions.resume(assetId ? [assetId] : []);
      }
      return Expressions.resume([]);
    },
    Literal: ({name, value}) => {
      if (namesEqual(name, imageType)) {
        const id = (value as {id: string})?.id;
        return Expressions.resume(id ? [id] : []);
      }
      if (namesEqual(name, lottiePathType) || namesEqual(name, rivePathType)) {
        const src = (value as LottiePath | RivePath)?.src;
        const id =
          (value as LottiePath | RivePath)?.id ?? getAssetIdFromSrc(src);
        return Expressions.resume(id ? [id] : []);
      }
      return Expressions.resume([]);
    },
    Typed: ({type}) => {
      const ids = getAssetIdsFromDataType(type);
      return Expressions.resume(ids);
    },
    _: constant(Expressions.resume([])),
  })
);

export const updateAssetIdsInDataType = (
  dataType: Type,
  assetIdMap: {[id: string]: string}
): Type => {
  const fields = Types.getRecordFields(dataType);
  if (!fields) {
    return dataType;
  }

  return Object.entries(fields).reduce((nextType, [fieldKey, field]) => {
    if (isImageType(field)) {
      return Types.updateRecordField(nextType, fieldKey, (type) => {
        const defaultValue: {id: string} | null = Types.getDefaultValue(
          type,
          null
        );

        const {id, ...rest} = defaultValue || {};
        if (!id) {
          return type;
        }

        const nextAssetId = assetIdMap[id];
        if (!nextAssetId) {
          return type;
        }

        const nextDefaultValue = {id: nextAssetId, ...rest};
        return withSystem(
          {...getSystem(type), defaultValue: nextDefaultValue},
          type
        );
      });
    }

    // Here we can add other types that need to be updated like Lottie

    return Types.updateRecordField(nextType, fieldKey, identity);
  }, dataType);
};

/**
 * Component-aware asset id remap: rewrites image asset ids on the
 * component's render and — when the component is enrolled in `defaultData` —
 * on its `defaultData` properties. For non-enrolled components, falls
 * through to `updateAssetIdsInDataType` so `system.defaultValue` still gets
 * remapped.
 */
export const remapAssetIdsInComponent = (
  plugins: Plugins.PluginsType,
  component: Component.ComponentType,
  assetIdMap: {[id: string]: string},
  assetsToImport?: AssetsType
): Component.ComponentType => {
  const render = Component.getRender(component);
  const nextRender = updateAssetIdsInExpression(
    render,
    assetIdMap,
    assetsToImport
  );
  const withRender = mapSlotDefaultLists(
    Component.update(component, {render: nextRender}),
    (list) =>
      getAssetIdsFromExpression(list).length > 0
        ? updateAssetIdsInExpression(list, assetIdMap, assetsToImport)
        : list
  );

  const currentDefaultData = Component.getDefaultData(component);

  if (currentDefaultData) {
    return updateDefaultDataPropertiesOnComponent(
      withRender,
      (valueExpr) => {
        const literal = Expressions.getLiteral(valueExpr);
        if (!literal || !namesEqual(literal.name, imageType)) return valueExpr;
        const valueObject = getObject(literal.value);
        if (!valueObject || !valueObject.id) return valueExpr;
        const id = getString(valueObject.id);
        if (!id) return valueExpr;
        const nextId = assetIdMap[id];
        if (!nextId || nextId === id) return valueExpr;
        return ELiteral({
          name: imageType,
          value: {...valueObject, id: nextId},
        });
      },
      plugins
    );
  }

  const dataType = Component.getDataType(component);
  const nextDataType = updateAssetIdsInDataType(dataType, assetIdMap);
  return Component.update(withRender, {dataType: nextDataType});
};

function updateBackgroundVideoUrls(
  element: ExpressionElement,
  assetsToImport?: AssetsType
) {
  if (!element?.data) {
    return EElement(element);
  }

  try {
    // Get poster URL pointer and validate
    const posterUrlPointer = Expressions.getIn(element.data, [
      {in: 'Record', at: 'bgvideo'},
      {in: 'Record', at: 'poster_image_url'},
    ]);
    if (!posterUrlPointer) {
      return EElement(element);
    }

    // Get poster URL and validate
    const posterUrl = Expressions.getText(posterUrlPointer);
    if (!posterUrl) {
      return EElement(element);
    }

    // Get asset IDs and validate
    const oldAssetId = getAssetIdFromSrc(posterUrl);
    const newAsset: FileRecordType | undefined = oldAssetId
      ? assetsToImport?.get(oldAssetId)?.toJS()
      : undefined;
    if (!oldAssetId || !newAsset || !newAsset.variants?.length) {
      return EElement(element);
    }

    // Find poster image
    const posterVariant = newAsset.variants.find(
      (v) => v?.format && Object.keys(SUPPORTED_IMAGE_TYPES).includes(v.format)
    );
    if (!posterVariant?.cdnUrl) {
      return EElement(element);
    }

    // Find video urls
    const videoVariants = newAsset.variants.reduce<string[]>((urls, v) => {
      if (
        v.format &&
        v.cdnUrl &&
        Object.keys(SUPPORTED_VIDEO_TYPES).includes(v.format)
      ) {
        urls.push(v.cdnUrl);
      }
      return urls;
    }, []);
    if (!videoVariants.length) {
      return EElement(element);
    }

    // Update poster image URL
    let updatedData = Expressions.setIn(
      element.data,
      [
        {in: 'Record', at: 'bgvideo'},
        {in: 'Record', at: 'poster_image_url'},
      ],
      EText(posterVariant.cdnUrl)
    );

    // Update video URLs
    updatedData = Expressions.setIn(
      updatedData,
      [
        {in: 'Record', at: 'bgvideo'},
        {in: 'Record', at: 'video_urls'},
      ],
      EList(
        videoVariants.map((v) => {
          return EText(v);
        })
      )
    );

    const newElement: ExpressionElement = {...element, data: updatedData};
    return EElement(newElement);
  } catch (error) {
    return EElement(element);
  }
}

export function updateAssetIdsInExpression(
  expression: Expression,
  assetIdMap: {
    [key: string]: string;
  },
  assetsToImport?: AssetsType
): Expression {
  return Expressions.everywhere(
    matchExpression({
      Element: (element: ExpressionElement) => {
        const {type} = element;
        if (namesEqual(type, backgroundVideoType)) {
          return updateBackgroundVideoUrls(element, assetsToImport);
        }
        return EElement(element);
      },
      Literal: ({name, value}) => {
        if (namesEqual(name, imageType)) {
          const {id, ...rest} = value as {id: string} & Record<string, unknown>;
          const newId = assetIdMap[id] || id;
          return ELiteral({name, value: {id: newId, ...rest}});
        }
        if (
          namesEqual(name, lottiePathType) ||
          namesEqual(name, rivePathType)
        ) {
          const {src, ...rest} = value as LottiePath | RivePath;
          const id = getAssetIdFromSrc(src);
          if (!id) {
            return ELiteral({name, value});
          }
          return ELiteral({name, value: {src: assetIdMap[id] || src, ...rest}});
        }
        return ELiteral({name, value});
      },
      Typed: ({type, expr}) => {
        const dataType = updateAssetIdsInDataType(type, assetIdMap);
        return ETyped({type: dataType, expr});
      },
      _: identity,
    })
  )(expression);
}

export const findExistingLibraryAsset = ({
  fileHash,
  originPluginNames,
  assets,
}: {
  fileHash: RawFileType['fileHash'];
  originPluginNames: PluginName[];
  assets: AssetsType;
}) => {
  return assets.find((currentAsset) => {
    const isFileHashEqual = fileHash === currentAsset.fileHash;

    const isOriginEqual =
      // both null-ish
      (!originPluginNames && !currentAsset.origin) ||
      // both defined and equal
      Boolean(
        originPluginNames &&
        currentAsset.origin &&
        originPluginNames.includes(getPluginName(currentAsset.origin))
      );

    return isFileHashEqual && isOriginEqual;
  });
};

export function buildLibraryAssetManagementMap({
  assetsInState,
  assetsToImport = [],
  assetsToDetach = new Set(),
  assetsToDelete = new Set(),
  assetsToDuplicate = new Set(),
  siteId,
}: {
  assetsInState: AssetsType;
  assetsToImport?: RawFileType[];
  assetsToDuplicate?: Set<RawFileType['_id']>;
  assetsToDetach?: Set<RawFileType['_id']>;
  assetsToDelete?: Set<RawFileType['_id']>;
  siteId?: string;
}): LibraryAssetManagementMap {
  const assetMap = {} as LibraryAssetManagementMap;

  // Add all assets to import or update
  assetsToImport.forEach((asset) => {
    // If we can find an asset in state we can update it instead of importing a new one
    const reusableAsset = assetsInState.get(asset._id);

    if (reusableAsset) {
      // If we can use it flag it for update instead.
      assetMap[reusableAsset._id] = {
        value: {
          ...asset,
          _id: reusableAsset._id,
          versionedResource: null,
          sharedResourceProfile: null,
          marketplaceResourceProfile: null,
          origin: asset.origin!,
        },
        update: true,
      };
    } else {
      // Otherwise, it is either a brand new asset or an asset that we can't
      // reuse, so we import it
      assetMap[asset._id] = {
        value: {
          ...asset,
          _id: asset._id,
          versionedResource: null,
          sharedResourceProfile: null,
          marketplaceResourceProfile: null,
          origin: asset.origin!,
        },
        import: true,
      };
    }
  });

  // Add all assets to detach
  assetsToDetach.forEach((assetId) => {
    const asset = assetsInState.get(assetId);
    if (!asset) {
      // This should never be the case?
      return;
    }

    assetMap[assetId] = {
      value: {
        ...asset.toJS(),
        site: siteId,
        versionedResource: null,
        sharedResourceProfile: null,
        marketplaceResourceProfile: null,
        _id: asset._id,
        origin: null,
      },
      update: true,
    };
  });

  assetsToDuplicate.forEach((assetId) => {
    const asset = assetsInState.get(assetId);
    if (!asset) {
      // This should never be the case?
      return;
    }

    assetMap[assetId] = {
      value: {
        ...asset.toJS(),
        ...(siteId && {site: siteId}),
        origin: null,
        versionedResource: null,
        sharedResourceProfile: null,
        marketplaceResourceProfile: null,
        markedAsDeleted: false,
      },
      duplicate: true,
    };
  });

  // Flag assets for deletion
  assetsToDelete.forEach((assetId) => {
    assetMap[assetId] = {value: {_id: assetId}, delete: true};
  });

  return assetMap;
}

export function getAssetsToReuse(assetMap: LibraryAssetManagementMap) {
  return Object.entries(assetMap).reduce(
    (map, [id, item]) => {
      const canReuse =
        ('reuse' in item && item.reuse) || ('update' in item && item.update);
      if (canReuse) {
        map[id] =
          // The Animation::Animation element (used to render Lottie assets) does not references
          // the asset ID directly, like Images or Videos, but instead references the asset's CDN URL.
          // When building the assetMap we need to account for this case, since the assetMap will
          // eventually be used to remap asset references in the element's data, which in the case of
          // Lottie and Rive assets will be the asset's CDN URL, not the ID.
          isLottieAsset(item.value as FileRecord) ||
          isRiveAsset(item.value as FileRecord)
            ? (item.value as RawFileType).cdnUrl || ''
            : item.value._id;
      }
      return map;
    },
    {} as {[id: string]: string}
  );
}

export function getAssetsToDelete(assetMap: LibraryAssetManagementMap) {
  return Object.entries(assetMap ?? {}).reduce((assetIds, [assetId, item]) => {
    if ('delete' in item && item.delete) {
      assetIds.push(assetId);
    }
    return assetIds;
  }, [] as string[]);
}

export function getAssetsToDuplicate(assetMap: LibraryAssetManagementMap) {
  return Object.values(assetMap).reduce<RawFileType[]>((files, item) => {
    if ('duplicate' in item && item.duplicate) {
      files.push(item.value);
    }
    return files;
  }, []);
}
export function getAssetsToImport(assetMap: LibraryAssetManagementMap) {
  return Object.values(assetMap).reduce((files, item) => {
    if ('import' in item && item.import) {
      files.push(item.value);
    }
    return files;
  }, [] as RawFileType[]);
}

export function getAssetsToUpdate(assetMap: LibraryAssetManagementMap) {
  return Object.values(assetMap).reduce(
    (files, item) => {
      if ((item as {update: boolean}).update) {
        files[item.value._id] = item.value as RawFileType;
      }
      return files;
    },
    {} as {[id: string]: RawFileType}
  );
}

export const ASSET_ID_REGEX = /(?:\/|%2F)([0-9a-f]{24})(?=_)/;

function collectAssetIdFromJSONNode(
  node: JSONNode,
  srcPath: string,
  idPath?: string
) {
  if (idPath) {
    const assetId = _.get(node, idPath, '');
    if (assetId) {
      return assetId;
    }
  }

  const assetSrc = _.get(node, srcPath, '');
  return getAssetIdFromSrc(assetSrc) || '';
}

type LightboxJsonItem = {
  val: {
    image?: {
      _id?: string;
      url?: string;
    };
  };
};

function determineLightboxSymbolAssets(symbol: JSONNode): string[] {
  const assetIds: string[] = [];
  symbol.data?.json?.items?.forEach((item: LightboxJsonItem) => {
    const assetId =
      item.val?.image?._id ?? getAssetIdFromSrc(item.val?.image?.url ?? '');
    if (assetId) {
      assetIds.push(assetId);
    }
  });

  return assetIds;
}

function collectAssetIdsFromDataTypes(
  jsonNode: JSONNode,
  componentMap: Component.ComponentMap
): string[] {
  const assetIds: string[] = [];
  if (jsonNode.dataType) {
    // this is a root component
    const dataType = parseDataType(jsonNode.dataType);
    if (dataType) {
      assetIds.push(...getAssetIdsFromDataType(dataType));
    }
  }

  const rawDefaultData = jsonNode.data?.sym?.defaultData;
  if (rawDefaultData !== undefined) {
    const defaultData = parseDefaultData(rawDefaultData);
    if (defaultData) {
      assetIds.push(...getAssetIdsFromExpression(defaultData));
    }
  }

  if (jsonNode.data?.sym?.inst) {
    // this is a component instance
    const rootComponent = componentMap.get(
      qualify('__SitePlugin', Component.name(jsonNode.data.sym.inst))
    );
    if (!rootComponent) {
      return assetIds;
    }

    const overrides = jsonNode.data.sym.overrides;
    assetIds.push(
      ...getAssetIdsFromDataType(
        Component.getDataType(rootComponent),
        overrides
      )
    );
  }

  return assetIds;
}

export function collectAssetIdsFromJSONNode(
  jsonNode: JSONNode,
  componentMap: Component.ComponentMap
): string[] {
  const assetIds: string[] = [];
  switch (jsonNode.type) {
    case 'Image':
      assetIds.push(jsonNode.data?.img?.id);
      break;
    case 'BackgroundVideoWrapper':
      assetIds.push(collectAssetIdFromJSONNode(jsonNode, BG_VIDEO_SRC_PATH));
      break;
    case 'LightboxWrapper':
      assetIds.push(...determineLightboxSymbolAssets(jsonNode));
      break;
    case 'Animation': // Lottie
      assetIds.push(
        collectAssetIdFromJSONNode(jsonNode, LOTTIE_SRC_PATH, LOTTIE_ID_PATH)
      );
      break;
    case 'Rive':
      assetIds.push(
        collectAssetIdFromJSONNode(jsonNode, RIVE_SRC_PATH, RIVE_ID_PATH)
      );
      break;
  }

  assetIds.push(...collectAssetIdsFromDataTypes(jsonNode, componentMap));
  return assetIds;
}

function parseDataType(rawDataType: Type | string): Type | null {
  try {
    return typeof rawDataType === 'string'
      ? JSON.parse(rawDataType)
      : rawDataType;
  } catch (err) {
    return null;
  }
}

/**
 * Get all Asset data (in POJO format) where `origin` equals the given pluginName.
 */
export function getAssetsByOrigin(
  pluginNames: PluginName[],
  assets: AssetsType
): AssetsType {
  const pluginNamesSet = new Set(pluginNames);
  return assets.filter((asset) =>
    Boolean(asset.origin && pluginNamesSet.has(getPluginName(asset.origin)))
  );
}

export function collectAssetsFromPlugin(
  plugin: PluginName,
  plugins: Plugins.PluginsType
) {
  const pluginAssets = Plugins.getPluginAssets(plugins);
  return Object.values(pluginAssets.byPlugin()[plugin] || {}).map((asset) => ({
    ...asset,
    origin: qualify(plugin, asset._id),
  }));
}

export function findAssetFromPlugin(
  plugin: PluginName,
  id: string,
  plugins: Plugins.PluginsType
) {
  return Plugins.getPluginAssets(plugins).get(qualify(plugin, id));
}

/**
 * Given a Plugins Map and a list of Components Qualified identifiers,
 * returns a list of RawFileType with the assets to import, collected
 * from the corresponding element presets.
 */
export function collectAssetsFromElementPreset(
  componentIdentifiers: Qualified<Component.ComponentName>[],
  plugins: Plugins.PluginsType
) {
  const assetsSeen = new Set<RawFileType['_id']>();
  const assetsToImport: RawFileType[] = [];
  componentIdentifiers.forEach(
    (
      // In this case componentName === elementPresetName and we
      // use that in our favor to get to the corresponding elementPreset
      // and assets
      qualifiedName
    ) => {
      // 0. Collect the Plugin's import strategy
      const importStrategy = Plugins.getElementPresetImportStrategy(
        plugins,
        qualifiedName
      );

      // 1. Perform a few checks so we can maybe return early
      const preset = Plugins.getElementPreset(plugins, qualifiedName);
      if (!preset) {
        return;
      }
      const presetAssets = Plugins.getElementPresetAssets(plugins, preset);
      if (!presetAssets) {
        return;
      }

      // 2. Collect assets from the elementPreset
      presetAssets.forEach((asset) => {
        if (
          !assetsSeen.has(asset._id) &&
          importStrategy === Plugins.IMPORT_STRATEGY.VALUE
        ) {
          assetsToImport.push({
            ...asset,
            origin: qualify(getPluginName(qualifiedName), asset._id),
          });
          assetsSeen.add(asset._id);
        }
      });
    }
  );
  return assetsToImport;
}

/**
 * Update newly added and detached components in componentMap
 * with imported assets ids. Updates components render expression
 * and dataType
 */
export function updateComponentMapWithImportedAssets({
  componentMap,
  assetMap,
  assetsToRemap,
  addedComponents = [],
  detachedComponents = [],
  plugins,
}: {
  componentMap: Component.ComponentMap;
  assetMap: LibraryAssetManagementMap;
  assetsToRemap: {[id: string]: {_id: string; cdnUrl?: string}};
  addedComponents?: Qualified<Component.ComponentName>[];
  detachedComponents?: Qualified<Component.ComponentName>[];
  plugins: Plugins.PluginsType;
}): Component.ComponentMap {
  const componentsToProcess =
    addedComponents.length + detachedComponents.length;
  const assetsToReuse = getAssetsToReuse(assetMap);
  const numAssetsToRemap =
    Object.values(assetsToReuse).length + Object.values(assetsToRemap).length;
  // 1. If we don't have any of these, just return early
  if (componentsToProcess === 0 || numAssetsToRemap === 0) {
    return componentMap;
  }

  // 2. Get all components to process
  const allComponentsToProcess: {
    component: Component.ComponentType;
    componentId: Qualified<Component.ComponentName>;
  }[] = [];
  addedComponents.forEach((origin) => {
    const {component, componentId} = getComponentByOrigin(origin, componentMap);
    if (!component || !componentId) {
      return;
    }
    allComponentsToProcess.push({component, componentId});
  });
  detachedComponents.forEach((componentId) => {
    const component = componentMap.get(componentId);
    if (!component) {
      return;
    }
    allComponentsToProcess.push({component, componentId});
  });

  const nextComponentMap_mut = componentMap.asMutableCopy();

  const managedAssetsMap = Object.entries(assetsToRemap).reduce(
    (map, [id, value]) => {
      // The Animation::Animation element (used to render Lottie assets) does not references
      // the asset ID directly, like Images or Videos, but instead references the asset's CDN URL.
      // When building the assetMap we need to account for this case, since the assetMap will
      // eventually be used to remap asset references in the element's data, which in the case of
      // Lottie assets will be the asset's CDN URL, not the ID.
      const assetId =
        isLottieAsset(value as FileRecord) || isRiveAsset(value as FileRecord)
          ? value.cdnUrl || ''
          : String(value._id);
      map[id] = assetId;
      return map;
    },
    {} as {[id: string]: string}
  );

  const assetIdMap = {
    ...assetsToReuse,
    ...managedAssetsMap,
  };

  // 3. Process each component
  allComponentsToProcess.forEach(({component, componentId}) => {
    const newComponent = remapAssetIdsInComponent(
      plugins,
      component,
      assetIdMap
    );

    nextComponentMap_mut.set_mut(componentId, newComponent);
  });

  return nextComponentMap_mut.asNonMutableCopy();
}

/**
 * Update style blocks from newly added and detached components,
 * or from on-demand materialized tag styles, with imported asset ids.
 */
export function updateStyleBlockStateWithImportedAssets({
  state,
  plugins,
  assetsToRemap,
  addedComponents = [],
  addedStyles = [],
  detachedComponents = [],
  assetMap,
  idMap,
}: {
  state: StyleBlockStateType;
  plugins: Plugins.PluginsType;
  assetsToRemap: {[id: string]: {_id: string; cdnUrl?: string}};
  assetMap: LibraryAssetManagementMap;
  idMap: {[id: string]: string};
  addedComponents?: Qualified<Component.ComponentName>[];
  addedStyles?: Qualified<string>[];
  detachedComponents?: Qualified<Component.ComponentName>[];
}): StyleBlockStateType {
  const componentsToProcess =
    addedComponents.length + detachedComponents.length;
  const stylesToProcess = addedStyles.length;
  const assetsToReuse = getAssetsToReuse(assetMap ?? {});
  const numAssetsToRemap =
    Object.values(assetsToReuse).length + Object.values(assetsToRemap).length;
  // 1. Early return if any of these is missing
  if (
    numAssetsToRemap === 0 ||
    (componentsToProcess === 0 && stylesToProcess === 0)
  ) {
    return state;
  }

  const idValues = ImmSet(Object.values(idMap ?? {}));

  // 2. We now index the style blocks by Plugin name.
  // We do this so we can quickly check later on if
  // a given style block should be updated or not.
  // With this information indexed we can do a single
  // pass over all style blocks.
  const styleBlockIdsIndexedByPluginName: {
    [pluginName: string]: ImmSet<RawStyleBlockType['_id']>;
  } = {};
  // Start iterating over the added components...
  addedComponents.forEach((origin) => {
    const elementPreset = Plugins.getElementPreset(plugins, origin);
    //...early return if the element preset is missing, which
    // should never be the case, but we maintain type-safety...
    if (!elementPreset) return;

    //...get a set of all the style block ids for the current element preset...
    const currentStyleBlockIds = ImmSet(
      elementPreset.styleBlocks?.map((styleBlock) => styleBlock._id) ?? []
    );

    const plugin = getPluginName(origin);
    const updatedStyleBlocksSet =
      //...start a new Set if one has not been initialized yet...
      (styleBlockIdsIndexedByPluginName[plugin] ?? ImmSet())
        //...perform a union with the current style block ids...
        .union(currentStyleBlockIds);

    //...and update the index.
    styleBlockIdsIndexedByPluginName[plugin] = updatedStyleBlocksSet;
  });

  addedStyles.forEach((origin) => {
    const plugin = getPluginName(origin);
    const styleBlockId = getName(origin);
    styleBlockIdsIndexedByPluginName[plugin] = (
      styleBlockIdsIndexedByPluginName[plugin] ?? ImmSet()
    ).add(styleBlockId);
  });

  const assetIdMap = ImmMap({
    ...assetsToReuse,
    ...Object.fromEntries(
      Object.entries(assetsToRemap).map(([key, value]) => [key, value._id])
    ),
  });

  // Helper utility
  const shouldStyleBlockBeUpdated = (styleBlock: StyleBlock) => {
    const {origin: styleBlockOrigin, _id: id} = styleBlock;

    // idMap essentially contains all the style blocks IDs we care about,
    // because we append the copied style block IDs to it before dispatching
    // the action. So we can derive which style blocks should be touched
    // during this operation.
    const isDirty = idValues.includes(id);
    if (isDirty) {
      return true;
    }

    // If the style block has no origin, we should definitely not update it.
    if (!styleBlockOrigin) {
      return false;
    }

    const originPluginName = getPluginName(styleBlockOrigin);
    const originStyleBlocksSet =
      styleBlockIdsIndexedByPluginName[originPluginName];

    // This will be the case if style block has originated from a plugin
    // that has not been updated this time. Skip it.
    if (!originStyleBlocksSet) {
      return false;
    }

    const originStyleBlockId = getName(styleBlockOrigin);
    return originStyleBlocksSet.has(originStyleBlockId);
  };

  // 3. With the index metadata in place, we can now iterate over all style blocks
  const nextStyleBlocks = state
    .get('styleBlocks', ImmMap())
    .map((styleBlock) => {
      if (!shouldStyleBlockBeUpdated(styleBlock)) {
        return styleBlock;
      }
      return replaceBackgroundImages(assetIdMap)(styleBlock);
    });
  return state.set('styleBlocks', nextStyleBlocks);
}

/**
 * Builds a hash map of received assets
 */
export function getAssetsHashMap(assets: RawFileType[]): LibraryExportsHashMap {
  return assets.reduce<LibraryExportsHashMap>((memo, asset) => {
    memo[asset._id] = getAssetHash(asset);
    return memo;
  }, {});
}

function getAssetHash({
  fileHash,
  origFileName,
  alt,
  folderPath,
}: Pick<
  RawFileType,
  'fileHash' | 'alt' | 'origFileName' | 'folderPath'
>): LibraryExportHash {
  const contentsHash = fileHash;
  return {
    contentsHash,
    metadataHash: simpleHash({
      origFileName,
      alt: alt ?? '',
      folderPath:
        folderPath && folderPath.length > 0
          ? folderPath[folderPath.length - 1]?.id
          : undefined,
    }),
  };
}

type FolderPath = {id: FolderT['_id']; name: FolderT['name']}[];
type FolderPathMap = Record<FolderT['_id'], FolderPath>;

/**
 * For each folder, the corresponding path is built by traversing the
 * parent tree and concatenating the names of each folder.
 *
 * @param assetFolders - An array of FolderT objects representing asset folders.
 * @return FolderPathMap - A map of folder IDs to their corresponding paths.
 */
export function getFolderPathMap(assetFolders: FolderT[]): FolderPathMap {
  // Thi map is used to easily find first level parents
  const folderMap = keyBy(assetFolders, '_id');

  return assetFolders.reduce<FolderPathMap>((folderPathMap, folder) => {
    const path: FolderPath = [];
    let currentFolder: FolderT | null | undefined = folder;

    // Prevent infinite loops
    const SAFETY_LIMIT = 1000;
    let safetyCounter = 0;

    while (currentFolder && safetyCounter <= SAFETY_LIMIT) {
      if (safetyCounter >= SAFETY_LIMIT) {
        throw new Error(
          '[getFolderPathMap] Infinite loop detected when building folder paths'
        );
      }
      // Add the current folder name to the path
      path.unshift({id: currentFolder._id, name: currentFolder.name});
      // Find parent and set as curent folder, if it exists
      currentFolder = folderMap[String(currentFolder.parent)];
      safetyCounter++;
    }
    folderPathMap[folder._id] = path;

    return folderPathMap;
  }, {});
}

/**
 * Builds a FolderPathMap from the received folders list.
 * Iterates over each member (asset) of each folder, hydrating
 * with the folderPath property recovered from the FolderPathMap.
 *
 * @param assets - An array of RawFileType objects representing assets.
 * @param assetFolders - An array of FolderT objects representing asset folders.
 * @returns An array of RawFileType objects with the folderPath property added.
 */
export function hydrateAssetsWithFolderPath(
  assets: RawFileType[],
  assetFolders: FolderT[]
): RawFileType[] {
  // No need to build any maps if there are no assets or folders
  if (!assets.length || !assetFolders.length) {
    return assets;
  }

  const folderPathMap = getFolderPathMap(assetFolders);
  const assetsMap = keyBy(assets, '_id');

  assetFolders.forEach((folder) => {
    folder.members.forEach((member) => {
      const assetId = member.toString();
      if (assetsMap[assetId]) {
        assetsMap[assetId].folderPath = folderPathMap[folder._id];
      }
    });
  });

  return Object.values(assetsMap);
}

/**
 * @deprecated
 * Cleanup tracked with https://webflow.atlassian.net/browse/SCALE-1422
 */
export function maybePatchAnimationNodeAssetsV1(
  jsonNode: JSONNode,
  assetIdMap: AssetMap,
  path: string,
  getValue: (asset: AssetMap[string]) => string | undefined
): JSONNode {
  // Check if the src we're trying to update is actually
  // present in the node.
  const assetSrc = get(jsonNode, path);
  if (!assetSrc) {
    return jsonNode;
  }

  // Get the asset ID out of the src path.
  const assetId = getAssetIdFromSrc(assetSrc);
  // Early return if no asset ID found
  if (!assetId) {
    return jsonNode;
  }
  // Get the asset from the asset map
  const asset = assetIdMap[assetId];
  if (!asset) {
    return jsonNode;
  }

  const newValue = getValue(asset);
  if (newValue) {
    // Update the field in the path with the new value.
    set(jsonNode, path, newValue);
  }

  return jsonNode;
}

export function maybePatchAnimationNodeAssets(
  jsonNode: JSONNode,
  path: string
): JSONNode {
  // Check if the src we're trying to update is actually
  // present in the node.
  const assetSrc = get(jsonNode, path);
  if (!assetSrc) {
    return jsonNode;
  }

  // Get the asset ID out of the src path.
  const assetId = getAssetIdFromSrc(assetSrc);
  if (!assetId) {
    return jsonNode;
  }

  // Replace URL with ID
  set(jsonNode, path, assetId);

  return jsonNode;
}

const VALID_ASSET_TYPES = getSupportedAssetTypes();

export function isAssetExport({
  origFileName,
  mimeType,
}: Pick<RawFileType, 'origFileName' | 'mimeType'>) {
  // Only a subset of assets should actually be exported as Library Assets.
  // Fonts, Icons and Videos are all stored in the same `files` collection
  // so we need to filter them out here.
  const extension = getExtension(origFileName);
  const isValidAssetExport = VALID_ASSET_TYPES.has(extension);
  if (!isValidAssetExport) {
    return false;
  }

  // BG Videos should not be included in the exports list.
  const isAVideo =
    Object.keys(SUPPORTED_VIDEO_TYPES).includes(extension) &&
    // OGG files can be video or audio, so we need to check the mime type
    mimeType.startsWith('video');
  if (isAVideo) {
    return false;
  }

  return true;
}

export function getAssetExportsFromAssets(
  assets: Array<RawFileType>
): Array<AssetExport> {
  return assets.reduce<AssetExport[]>((acc, asset) => {
    if (isAssetExport(asset)) {
      acc.push({
        type: LIBRARY_EXPORT_TYPES.ASSET,
        metadata: {
          targetId: String(asset._id),
          displayName: asset.origFileName,
        },
      });
    }

    return acc;
  }, []);
}

function getAssetIdFromExpressionPath(
  element: ExpressionElement,
  path: ExpressionPath
): string | null {
  const literal = Expressions.getIn(Expressions.getElementData(element), path);
  if (!literal) {
    return null;
  }

  const json = Expressions.getLiteral(literal);
  if (!json) {
    return null;
  }

  const jsonObject = getObject(json.value);
  if (jsonObject && jsonObject.id) {
    return getString(jsonObject.id) ?? null;
  }

  return null;
}

export function getElementAssetId(element: ExpressionElement): string | null {
  if (isImageElement(element)) {
    return getAssetIdFromExpressionPath(element, IMAGE_ASSET_EXPRESSION_PATH);
  }

  if (isLottieElement(element)) {
    return getAssetIdFromExpressionPath(element, LOTTIE_ASSET_EXPRESSION_PATH);
  }

  if (isRiveElement(element)) {
    return getAssetIdFromExpressionPath(element, RIVE_ASSET_EXPRESSION_PATH);
  }

  return null;
}
